import { useAuthStore } from '../stores/auth';
import { trackApiCall, captureError, Sentry } from './sentry';
import { config } from '../config';

const API_URL = config.apiUrl;

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = useAuthStore.getState().token;
  const method = options.method || 'GET';
  const startTime = performance.now();

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    const duration = performance.now() - startTime;
    trackApiCall(method, endpoint, response.status, duration);

    if (response.status === 401) {
      useAuthStore.getState().logout();
      const error = new Error('Session expired');
      Sentry.setContext('api_error', { endpoint, status: 401 });
      captureError(error, { endpoint, status: 401 });
      throw error;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
      const error = new Error(errorData.message || 'Request failed');
      Sentry.setContext('api_error', {
        endpoint,
        status: response.status,
        body: errorData,
        duration
      });
      captureError(error, { endpoint, status: response.status, errorData });
      throw error;
    }

    return response.json();
  } catch (error) {
    const duration = performance.now() - startTime;
    trackApiCall(method, endpoint, 0, duration);

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      Sentry.setContext('network_error', { endpoint, method });
      captureError(error, { endpoint, method, type: 'network_error' });
    }

    throw error;
  }
}

export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint),
  post: <T>(endpoint: string, data: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  put: <T>(endpoint: string, data: unknown) =>
    apiRequest<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  patch: <T>(endpoint: string, data: unknown) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'DELETE' }),
};
